import { useState } from "react";
import { AlertOctagon, Loader2, RotateCcw } from "lucide-react";
import { api } from "../../lib/config";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";

export const SquareResetPanel = () => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/square/reset/preview");
      setPreview(data);
      setConfirmation("");
      setOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "No se pudo preparar la limpieza");
    } finally {
      setLoading(false);
    }
  };

  const executeReset = async () => {
    if (!preview || confirmation !== preview.confirmation_phrase) return;
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/square/reset/execute", {
        preview_token: preview.preview_token,
        confirmation,
      });
      toast.success(`Historial Square eliminado. Balance recalculado: $${data.amount_removed}.`);
      setOpen(false);
      window.location.reload();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "No se pudo completar la limpieza");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <section className="mt-10 border-y border-red-200 bg-red-50 px-4 py-5" data-testid="square-reset-panel">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 max-w-7xl mx-auto">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-red-700 font-bold">Herramienta administrativa</div>
          <h2 className="font-heading text-2xl text-red-950">Reiniciar historial local de Square</h2>
          <p className="text-sm text-red-800 max-w-3xl">
            Elimina eventos, pagos sin asignar y movimientos automáticos de Square. Conserva inscripciones, pagos manuales, becas y logística.
          </p>
        </div>
        <Button variant="destructive" onClick={loadPreview} disabled={loading} data-testid="square-reset-preview-button">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
          Revisar antes de borrar
        </Button>
      </div>

      <AlertDialog open={open} onOpenChange={(next) => !executing && setOpen(next)}>
        <AlertDialogContent data-testid="square-reset-dialog" className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertOctagon className="w-5 h-5" /> Borrado permanente de Square
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción solo afecta el historial local asociado automáticamente a Square. No borra inscripciones ni pagos verificados manualmente.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm" data-testid="square-reset-counts">
                <Count label="Eventos webhook" value={preview.counts.webhook_events} />
                <Count label="Transacciones Square" value={preview.counts.square_transactions} />
                <Count label="Pagos sin asignar" value={preview.counts.unmatched_payments} />
                <Count label="Personas recalculadas" value={preview.counts.registrations_to_recalculate} />
              </div>
              <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-900">
                Se retirarán <b data-testid="square-reset-amount">${preview.counts.square_amount_to_remove}</b> aplicados automáticamente por Square.
                Se conservarán <b>{preview.counts.manual_transactions_preserved}</b> movimientos manuales.
              </div>
              <div>
                <label htmlFor="square-confirmation" className="text-xs font-bold uppercase tracking-widest text-gray-700">
                  Escribe: {preview.confirmation_phrase}
                </label>
                <Input
                  id="square-confirmation"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  data-testid="square-reset-confirmation-input"
                  className="mt-2"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={executing} data-testid="square-reset-cancel">Cancelar</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={executeReset}
              disabled={executing || !preview || confirmation !== preview.confirmation_phrase}
              data-testid="square-reset-execute-button"
            >
              {executing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Borrar y recalcular
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

const Count = ({ label, value }) => (
  <div className="border border-gray-200 bg-white p-3 rounded-md">
    <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
    <div className="font-heading text-2xl text-black">{value}</div>
  </div>
);